import { useState } from "react";
import * as React from "react";
import {
    useApproveRulePublish,
    useGetFilterRulePublish,
    useRulesPublish,
} from "@/hooks/useLocalStorage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
    TreePine,
    List,
    Check,
    X,
} from "lucide-react";
import {
    RulePublish,
} from "../../shared/schema";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { Popup } from "@/widgets/RuleDetailDailog";
import { FilterTypeSearch } from "@/widgets/searchFilterType";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { useBreadcrumbs } from "../BreadcrumbContext";

interface HierarchicalNodeProps {
    item: RulePublish;
    level?: number;
}

interface TableHierarchicalNodeProps {
    topLevelTypesData: RulePublish[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (code: number) => void;
}

function TableRulePublish({
    topLevelTypesData,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
}: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<RulePublish[]>([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [showPopup, setShowPopup] = React.useState(false);
    const [dataHead, setDataHead] = React.useState<RulePublish>();
    const [dataContent, setDataContent] = React.useState<string>("");
    const [popupName, setPopupName] = React.useState("");
    const [itemToApprove, setItemToApprove] = useState<RulePublish | null>(null);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);

    const approveRulePublishMutation = useApproveRulePublish();

    React.useEffect(() => {
        if (topLevelTypesData) {
            setCurrentData(topLevelTypesData);
        }
    }, [topLevelTypesData]);

    function formatDate(dateString: string): string {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return "";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    }

    const ruleTypeOptions = [
        { value: "0", label: "Eligibility Rules" },
        { value: "1", label: "Pricing Rules" },
        { value: "2", label: "Credit Rating Rules" },
        { value: "4", label: "Workflow Entry Conditions" },
        { value: "5", label: "Compliance Rules" },
        { value: "6", label: "Form Rules" },
    ];

    const getRuleTypeLabel = (typeValue: string) => {
        const option = ruleTypeOptions.find((opt) => opt.value === typeValue);
        return option ? option.label : "";
    };

    function handleApprove(e: React.MouseEvent, item: RulePublish) {
        e.stopPropagation();
        setItemToApprove(item);
        setApproveDialogOpen(true);
    }

    function handleConfirmApprove() {
        if (!itemToApprove) return;

        approveRulePublishMutation.mutate({
            key: itemToApprove?.Id,
            status: itemToApprove?.Approved,
        });
    }

    React.useEffect(() => {
        if (approveRulePublishMutation.isSuccess === true) {
            setApproveDialogOpen(false);
            setReqStatus(approveRulePublishMutation.isSuccess);
            setAlertMessage(`${itemToApprove?.Approved === 1 ? "Rule UnPublished Successfully!" : "Rule Published Successfully!"}`);
            setItemToApprove(null);
            setAlertOpen(true);
            approveRulePublishMutation.reset();
        } else if (approveRulePublishMutation.isError) {
            const raw = approveRulePublishMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(approveRulePublishMutation.isSuccess);
            setApproveDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            approveRulePublishMutation.reset();
        }
    }, [approveRulePublishMutation]);

    return (
        <div>
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <>
                                <TableHead>ID</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Rule Value</TableHead>
                                <TableHead>Version</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Version</TableHead>
                                <TableHead>Actions</TableHead>
                            </>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow key={data.Rule_Id}>
                                <TableCell className="font-mono">{data.Id}</TableCell>

                                <TableCell className="font-mono font-medium">
                                    {data.Name}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {getRuleTypeLabel(String(data.Type))}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <Button
                                        variant={"link"}
                                        onClick={() => {
                                            setPopupName("Raw with Value")
                                            setDataHead(data);
                                            setDataContent(data.Data);
                                            setShowPopup(true);
                                        }}
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.Version}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {formatDate(data.DateTimeChanged)}
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    {data.Approved === 1 ? (
                                        <Button variant={"link"}>
                                            <Check className="h-4 w-4 mr-1 text-green-800" />
                                        </Button>
                                    ) : (
                                        <Button variant={"link"}>
                                            <X className="h-4 w-4 mr-1 text-red-800" />
                                        </Button>
                                    )}
                                </TableCell>

                                <TableCell>
                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="min-w-6 h-6 p-0"
                                            onClick={(e) => handleApprove(e, data)}
                                        >
                                            <span className="text-sm">
                                                {data.Approved === 0 ? "Publish" : "Unpublish"}
                                            </span>
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="mt-2">
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

            <ConfirmationDialog
                open={approveDialogOpen}
                onOpenChange={setApproveDialogOpen}
                onConfirm={handleConfirmApprove}
                isPending={approveRulePublishMutation.isPending}
                heading={itemToApprove?.Approved === 1 ? "Confirm UnPublish Request" : "Confirm Publish Request"}
                message={`Are you sure you want to ${itemToApprove?.Approved === 1 ? "UnPublish" : "Publish"} this Rule?`}
                confirmLabel={`${itemToApprove?.Approved === 1 ? "UnPublish" : "Publish"}`}
            />

            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={dataHead}
                    content={dataContent}
                    onClose={() => setShowPopup(false)}
                />
            )}
        </div>
    );
}

function RulePublishHierarchicalNode({ item, level = 0 }: HierarchicalNodeProps) {

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const [showPopup, setShowPopup] = React.useState(false);
    const [dataHead, setDataHead] = React.useState<RulePublish>();
    const [dataContent, setDataContent] = React.useState<string>("");
    const [popupName, setPopupName] = React.useState("");
    const [itemToApprove, setItemToApprove] = useState<RulePublish | null>(null);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);

    const approveRulePublishMutation = useApproveRulePublish();

    const ruleTypeOptions = [
        { value: "0", label: "Eligibility Rules" },
        { value: "1", label: "Pricing Rules" },
        { value: "2", label: "Credit Rating Rules" },
        { value: "4", label: "Workflow Entry Conditions" },
        { value: "5", label: "Compliance Rules" },
        { value: "6", label: "Form Rules" },
    ];

    function handleApprove(e: React.MouseEvent, item: RulePublish) {
        e.stopPropagation();
        setItemToApprove(item);
        setApproveDialogOpen(true);
    }

    function handleConfirmApprove() {
        if (!itemToApprove) return;

        approveRulePublishMutation.mutate({
            key: itemToApprove?.Id,
            status: itemToApprove?.Approved,
        });
    }

    React.useEffect(() => {
        if (approveRulePublishMutation.isSuccess === true) {
            setApproveDialogOpen(false);
            setReqStatus(approveRulePublishMutation.isSuccess);
            setAlertMessage(`${itemToApprove?.Approved === 1 ? "Rule UnPublished Successfully!" : "Rule Published Successfully!"}`);
            setItemToApprove(null);
            setAlertOpen(true);
            approveRulePublishMutation.reset();
        } else if (approveRulePublishMutation.isError) {
            const raw = approveRulePublishMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(approveRulePublishMutation.isSuccess);
            setApproveDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            approveRulePublishMutation.reset();
        }
    }, [approveRulePublishMutation]);

    return (
        <div className="border bg-white border-gray-200 rounded-lg">
            <div
                className="flex items-center space-x-2 md:space-x-3 p-2 md:p-3 hover:bg-gray-50 cursor-pointer"
                style={{ paddingLeft: `${8 + level * 16}px` }}
            >
                <div className="flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 ml-4">
                            <span className="font-medium text-gray-900 truncate text-sm md:text-base">
                                {(item as RulePublish).Name}
                            </span>
                            <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                                {(item as RulePublish).Id}
                            </span>
                        </div>
                    </div>

                    {item?.Data && (
                        <Button
                            variant={"link"}
                            onClick={() => {
                                setPopupName("Raw with Value")
                                setDataHead(item);
                                setDataContent(item.Data);
                                setShowPopup(true);
                            }}
                        >
                            View
                        </Button>
                    )}
                </div>

                <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                    <Button
                        variant="link"
                        size="sm"
                        className="min-w-6 h-6 p-0"
                        onClick={(e) => handleApprove(e, item)}
                    >
                        <span className="text-sm">
                            {item.Approved === 0 ? "Publish" : "Unpublish"}
                        </span>
                    </Button>
                </div>

                <AlertDialog
                    open={alertOpen}
                    onOpenChange={setAlertOpen}
                    message={alertMessage}
                    status={reqStatus}
                />

                <ConfirmationDialog
                    open={approveDialogOpen}
                    onOpenChange={setApproveDialogOpen}
                    onConfirm={handleConfirmApprove}
                    isPending={approveRulePublishMutation.isPending}
                    heading={itemToApprove?.Approved === 1 ? "Confirm UnPublish Request" : "Confirm Publish Request"}
                    message={`Are you sure you want to ${itemToApprove?.Approved === 1 ? "UnPublish" : "Publish"} this Rule?`}
                    confirmLabel={`${itemToApprove?.Approved === 1 ? "UnPublish" : "Publish"}`}
                />

                {showPopup && (
                    <Popup
                        popupName={popupName}
                        heading={dataHead}
                        content={dataContent}
                        onClose={() => setShowPopup(false)}
                    />
                )}
            </div>
        </div>
    );
}

export function CreatePublishRule() {
    const [viewMode, setViewMode] = useState<"table" | "tree">("table");
    const [topLevelTypes, setTopLevelTypes] = useState<RulePublish[]>([]);

    const [typeInput, setTypeInput] = useState<string>("ALL");
    const [ruletype, setRuletype] = useState<number>(-2);
    const [typeFilter, setTypeFilter] = useState<string>("ALL");
    const [typeRule, setTypeRule] = useState<number>(-2);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterTypeData, setFilterTypeData] = useState<RulePublish[]>([]);
    const [filterName, setFilterName] = useState<string>("");

    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(10);

    const { data: types, isLoading } = useRulesPublish(
        pageIndex,
        pageSize,
        typeFilter === "ALL" ? undefined : typeFilter,
        typeRule
    );

    React.useEffect(() => {
        setTopLevelTypes(types?.items ?? []);
        setTotalCount(types?.total ?? 0);
    }, [types]);

    const { setBreadcrumbs } = useBreadcrumbs();

    React.useEffect(() => {
        setBreadcrumbs([
            {
                label: `Rules Publish`,
                path: `/rulepublish`,
            },
        ]);
    }, []);

    const { data: filterTypes, isFetching: isFilterFetching } = useGetFilterRulePublish(
        filterName.length >= 3 ? filterName : undefined,
    );

    React.useEffect(() => {
        if (filterTypes && filterTypes.length > 0) {
            setFilterTypeData(filterTypes)
        } else {
            setFilterTypeData([])
        }
    }, [filterTypes])

    // Check for navigation from search
    React.useEffect(() => {
        const selectedItemStr = localStorage.getItem("cm:selectedItem");
        if (selectedItemStr) {
            try {
                const selectedItem = JSON.parse(selectedItemStr);
                console.log("Navigate to item in TreeExplorer:", selectedItem);
                // Clear the selection after handling
                localStorage.removeItem("cm:selectedItem");

                // TODO: Expand tree to show the selected item
                // This could involve expanding parent nodes and highlighting the target
            } catch (error) {
                console.error("Error parsing selected item:", error);
                localStorage.removeItem("cm:selectedItem");
            }
        }
    }, []);

    const ruleTypeOptions = [
        { value: "0", label: "Eligibility Rules" },
        { value: "1", label: "Pricing Rules" },
        { value: "2", label: "Credit Rating Rules" },
        { value: "4", label: "Workflow Entry Conditions" },
        { value: "5", label: "Compliance Rules" },
        { value: "6", label: "Form Rules" },
    ];

    const handleSearch = () => {
        setTypeFilter(typeInput);
        setTypeRule(ruletype)
    };

    const handleRefresh = (item: boolean) => {
        setTypeInput("ALL");
        setTypeFilter("ALL");
        setRuletype(-2);
        setTypeRule(-2);
        if (item) setFilterOpen(false);
    };

    return (
        <div className="bg-gray-50 min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                            Rules Publish
                        </h2>
                        <p className="text-gray-600 mt-1 text-sm md:text-base">
                            Publish Rules with values
                        </p>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <Button
                                size="sm"
                                variant={viewMode === "table" ? "default" : "ghost"}
                                onClick={() => setViewMode("table")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <List className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Table</span>
                                <span className="sm:hidden">Table</span>
                            </Button>
                            <Button
                                size="sm"
                                variant={viewMode === "tree" ? "default" : "ghost"}
                                onClick={() => setViewMode("tree")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <TreePine className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Tree</span>
                                <span className="sm:hidden">Tree</span>
                            </Button>
                        </div>
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={() => setFilterOpen(true)}
                        >
                            Filter Published Rules
                        </Button>
                    </div>
                </div>
            </div>
            <div>
                {filterOpen &&
                    <Card className="mb-4 md:mb-6">
                        <CardContent className="pt-0">
                            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
                                <div className="w-[450px]">
                                    <FilterTypeSearch
                                        filterTypeData={filterTypeData}
                                        isLoading={isFilterFetching}
                                        value={typeInput}
                                        onChange={setTypeInput}
                                        onSearch={(query) => setFilterName(query)}
                                    />
                                </div>

                                <div className="w-[450px]">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full justify-between text-sm"
                                            >
                                                {ruleTypeOptions.find((opt) => Number(opt.value) === ruletype)?.label ||
                                                    "Select Rule Type"}
                                            </Button>
                                        </DropdownMenuTrigger>

                                        <DropdownMenuContent align="start" className="w-[240px]">
                                            <DropdownMenuRadioGroup
                                                value={ruletype?.toString() ?? ""}
                                                onValueChange={(val) =>
                                                    setRuletype(val ? Number(val) : -2)
                                                }
                                            >
                                                <DropdownMenuRadioItem value="">
                                                    All Types
                                                </DropdownMenuRadioItem>
                                                {ruleTypeOptions.map((option) => (
                                                    <DropdownMenuRadioItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </DropdownMenuRadioItem>
                                                ))}
                                            </DropdownMenuRadioGroup>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>


                            <div className="mt-4 flex justify-center space-x-2">
                                <Button onClick={handleSearch}>Search</Button>
                                <Button variant="outline" onClick={() => handleRefresh(false)}>
                                    Refresh
                                </Button>
                                <Button variant="outline" onClick={() => handleRefresh(true)}>
                                    Close
                                </Button>
                            </div>

                        </CardContent>
                    </Card>
                }
            </div>

            {!isLoading ? (
                viewMode === "table" ? (
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="space-y-2">
                                {topLevelTypes && topLevelTypes.length > 0 ? (
                                    <>
                                        <TableRulePublish
                                            topLevelTypesData={topLevelTypes}
                                            pageIndex={pageIndex}
                                            pageSize={pageSize}
                                            totalCount={totalCount}
                                            onPageChange={setPageIndex}
                                        />
                                    </>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No Published Rule found. Create to get started.</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="space-y-2">
                                {topLevelTypes && topLevelTypes.length > 0 ? (
                                    topLevelTypes.map((type) => (
                                        <RulePublishHierarchicalNode key={type.Id} item={type} />
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No Published Rule found! Create to get started.</p>
                                    </div>
                                )}
                                <PaginationControl
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                />
                            </div>
                        </CardContent>
                    </Card>
                )
            ) : (
                <div className="p-6">Loading...</div>
            )}
        </div>
    );
}
